import React, { useEffect, useRef, useState } from "react"
import ReactDom from "react-dom"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import Button from "../../button/button"
import styles from "./importDataModal.module.css"
import { validateFile, uploadGeneric } from "../../../services/import"
import { FILES_MAPPING } from "../../../utils/pagesMapper"
import { getExpectedColumns, parseFilePreview } from "../../../utils/importColumns"
import { useImportFlow } from "../../../context/ImportFlowContext"
import DownloadIcon from "../../../assets/icons/download.svg"
import SparklesIcon from "../../../assets/icons/sparkles.svg"
import DocumentCirclePlusIcon from "../../../assets/icons/document-circle-plus.svg"

// Ícones inline usados só na Importação Inteligente (cor herdada via currentColor).
const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
)

const UploadIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
)

const FileIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
)

const CheckIcon = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const RING_CIRCUMFERENCE = 2 * Math.PI * 46

const formatBytes = (bytes) => {
  if (bytes == null) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const ImportDataModal = ({ onClose, fileType, institutionId }) => {
  const navigate = useNavigate()
  const { setImportData } = useImportFlow()

  const isSmart = fileType === "inteligente"

  const fileInputRef = useRef(null)
  const [selectedFile, setSelectedFile] = useState(null)
  // Importação Inteligente: estimativa de linhas do arquivo selecionado ({ loading, approxRows }).
  const [fileStats, setFileStats] = useState(null)
  const [isValidating, setIsValidating] = useState(false)
  const [validationError, setValidationError] = useState(null)
  // Importação Inteligente: progresso do upload (0-100) e tela final de sucesso (resultado do envio).
  const [uploadProgress, setUploadProgress] = useState(0)
  const [smartDone, setSmartDone] = useState(null)
  const progressTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current)
      }
    }
  }, [])
  // Importação Inteligente: quando marcado, o sistema pausa para revisão manual caso a IA fique
  // insegura no mapeamento; desmarcado (padrão), importa tudo direto sem devolver validação.
  const [manualValidation, setManualValidation] = useState(false)

  const handleButtonClick = () => {
    fileInputRef.current.click()
  }

  const applySelectedFile = (file) => {
    if (!file) {
      return
    }
    setSelectedFile(file)
    setValidationError(null)
    toast.success(`Arquivo "${file.name}" selecionado`)

    if (!isSmart) {
      return
    }

    // Estimativa aproximada de linhas para exibir ao usuário. A Importação Inteligente aceita
    // qualquer formato; se o preview falhar (ex.: delimitador fora do padrão), seguimos sem o número.
    setFileStats({ loading: true, approxRows: null })
    parseFilePreview(file)
      .then((preview) => {
        const approx = preview.estimatedTotal > 0 ? preview.estimatedTotal : null
        setFileStats({ loading: false, approxRows: approx })
      })
      .catch(() => {
        setFileStats({ loading: false, approxRows: null })
      })
  }

  const handleFileChange = (e) => {
    applySelectedFile(e.target.files[0])
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    applySelectedFile(e.dataTransfer.files[0])
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setFileStats(null)
    setValidationError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleImportAnother = () => {
    setSmartDone(null)
    setUploadProgress(0)
    handleRemoveFile()
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleValidateAndProceed = async () => {
    if (!selectedFile) {
      toast.error("Por favor, selecione um arquivo primeiro")
      return
    }

    try {
      setIsValidating(true)
      setValidationError(null)

      const preview = await parseFilePreview(selectedFile)
      const expectedColumns = getExpectedColumns(fileType)
      const fileHeaders = preview.headers
      const missingFields = expectedColumns.filter(col => !fileHeaders.includes(col))
      const extraFields = fileHeaders.filter(col => !expectedColumns.includes(col))

      const isValid = missingFields.length === 0 && extraFields.length === 0

      let validationResult
      let columnMapping = {}

      if (!isValid) {
        try {
          const firstDataLine = preview.rows.length > 0
            ? fileHeaders.map(header => preview.rows[0][header] || '').join(';')
            : ''

          const apiResult = await validateFile({
            expectedHeaders: expectedColumns,
            receivedHeaders: fileHeaders,
            firstDataLine: firstDataLine,
            institutionId: institutionId,
            fileType
          })

          if (apiResult.isSuccess) {
            const mappingFromApi = apiResult.fieldMapping || {}
            columnMapping = mappingFromApi

            if (apiResult.isValid) {
              validationResult = {
                isValid: true,
                message: apiResult.message || "Todos os campos foram mapeados com sucesso.",
                missingFields: [],
                extraFields: [],
                fieldMapping: mappingFromApi
              }
            } else {
              validationResult = {
                isValid: false,
                message: apiResult.message || "Alguns campos não puderam ser mapeados.",
                missingFields: apiResult.unmappedExpectedFields || [],
                extraFields: apiResult.unmappedReceivedFields || [],
                fieldMapping: mappingFromApi
              }
            }
          } else {
            throw new Error(apiResult.message || "Erro ao validar arquivo")
          }
        } catch (apiError) {
          const initialMapping = {}
          fileHeaders.forEach((header) => {
            if (expectedColumns.includes(header)) {
              initialMapping[header] = header
            }
          })
          columnMapping = initialMapping

          validationResult = {
            isValid: false,
            message: apiError.message || "Arquivo com problemas detectados localmente.",
            missingFields,
            extraFields
          }
        }
      } else {
        const initialMapping = {}
        fileHeaders.forEach((header) => {
          initialMapping[header] = header
        })
        columnMapping = initialMapping

        validationResult = {
          isValid: true,
          message: "Arquivo validado com sucesso! Todas as colunas estão corretas.",
          missingFields: [],
          extraFields: []
        }
      }

      setImportData({
        file: selectedFile,
        fileType,
        previewData: preview,
        validationResult,
        columnMapping,
        templateColumns: expectedColumns,
        institutionId
      })

      setIsValidating(false)
      onClose()
      navigate('/app/importar-dados/revisao')
    } catch (error) {
      setIsValidating(false)
      setValidationError(error.message || "Erro ao validar o arquivo.")
      toast.error(error.message || "Erro ao validar o arquivo.")
    }
  }

  // Importação Inteligente: o backend detecta os tipos e insere direto no banco,
  // sem retornar ao usuário para revisão.
  const handleSmartImport = async () => {
    if (!selectedFile) {
      toast.error("Por favor, selecione um arquivo primeiro")
      return
    }

    try {
      setIsValidating(true)
      setValidationError(null)
      setUploadProgress(0)

      // Progresso simulado com duração mínima: o envio real de arquivos pequenos é quase
      // instantâneo, então animamos um avanço até ~90% ao longo de ~3,6s e só completamos 100%
      // quando o envio termina (respeitando o tempo mínimo de tela). Arquivos grandes/lentos
      // seguem o tempo real: a barra fica em 90% até o envio concluir.
      const MIN_UPLOAD_MS = 3600
      const startedAt = Date.now()

      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current)
      }
      progressTimerRef.current = setInterval(() => {
        setUploadProgress((p) => Math.min(90, p + 5))
      }, 200)

      const result = await uploadGeneric({ file: selectedFile, institutionId, manualValidation })

      const elapsed = Date.now() - startedAt
      if (elapsed < MIN_UPLOAD_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_UPLOAD_MS - elapsed))
      }

      clearInterval(progressTimerRef.current)
      progressTimerRef.current = null
      setUploadProgress(100)
      // Segura 100% por um instante para o usuário perceber a conclusão do envio.
      await new Promise((resolve) => setTimeout(resolve, 700))

      setIsValidating(false)

      if (result?.needsFieldMapping) {
        // O agente não conseguiu mapear com confiança: a importação pausou e enviamos uma
        // notificação no sino para o usuário concluir o mapeamento na tela de revisão.
        toast.info("Não conseguimos mapear todas as colunas automaticamente. Enviamos uma notificação para você concluir o mapeamento.")
        onClose()
      } else if (result?.isBackground) {
        // Envio aceito; a IA mapeia em segundo plano. Mostramos a tela final de sucesso.
        setSmartDone(result)
      } else {
        const inserted = result?.totalInserted ?? 0
        const updated = result?.totalUpdated ?? 0
        // Mesmo toastId do evento SignalR (import-<correlationId>) para não duplicar o toast quando
        // o backend dispara o ImportCompleted da conclusão síncrona.
        toast.success(`Importação concluída! ${inserted} inserido(s), ${updated} atualizado(s).`,
          result?.correlationId ? { toastId: `import-${result.correlationId}` } : undefined)
        onClose()
      }
    } catch (error) {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current)
        progressTimerRef.current = null
      }
      setIsValidating(false)
      const msg = Array.isArray(error)
        ? error.join(' ')
        : (error?.message || (typeof error === 'string' ? error : "Falha ao importar o arquivo."))
      setValidationError(msg)
      toast.error(msg)
    }
  }

  // Importação Inteligente: descrição de linhas do arquivo, reutilizada nas telas de status.
  const smartRowsText = fileStats?.loading
    ? "calculando linhas…"
    : fileStats?.approxRows
      ? `~${fileStats.approxRows.toLocaleString("pt-BR")} linhas`
      : "linhas a processar"

  // Tela final: envio aceito, IA processando em segundo plano.
  if (isSmart && smartDone) {
    return ReactDom.createPortal(
      <div className={styles.modalBackdrop} onClick={onClose}>
        <div className={`${styles.modalContent} ${styles.smartStatus}`} onClick={(e) => e.stopPropagation()}>
          <span className={`${styles.statusIcon} ${styles.statusIconSuccess}`}>
            <CheckIcon />
          </span>
          <h3 className={styles.statusTitle}>Arquivo enviado com sucesso!</h3>
          <p className={styles.statusText}>
            Estamos mapeando os dados com a IA <strong>em segundo plano</strong>. Pode continuar
            usando o sistema — enviaremos uma <strong>notificação</strong> assim que a importação concluir.
          </p>

          {selectedFile && (
            <div className={styles.statusFileCard}>
              <span className={styles.selectedFileIcon}>
                <FileIcon />
              </span>
              <div className={styles.selectedFileMeta}>
                <strong title={selectedFile.name}>{selectedFile.name}</strong>
                <span>{smartRowsText} · {formatBytes(selectedFile.size)}</span>
              </div>
              <span className={styles.processingBadge}>Processando</span>
            </div>
          )}

          <div className={styles.statusActions}>
            <Button
              text="Importar outro arquivo"
              variation="secondary"
              size="big"
              onClick={handleImportAnother}
            />
            <Button
              text="Continuar usando o sistema"
              variation="primary"
              size="big"
              onClick={onClose}
            />
          </div>
        </div>
      </div>,
      document.getElementById("modal-root")
    )
  }

  if (isValidating) {
    // Importação Inteligente: tela de envio com barra de progresso.
    if (isSmart) {
      return ReactDom.createPortal(
        <div className={styles.modalBackdrop}>
          <div className={`${styles.modalContent} ${styles.smartStatus}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.uploadRingWrap}>
              <svg className={styles.uploadRing} viewBox="0 0 100 100">
                <circle className={styles.uploadRingTrack} cx="50" cy="50" r="46" />
                <circle
                  className={styles.uploadRingFill}
                  cx="50"
                  cy="50"
                  r="46"
                  style={{
                    strokeDasharray: RING_CIRCUMFERENCE,
                    strokeDashoffset: RING_CIRCUMFERENCE * (1 - uploadProgress / 100)
                  }}
                />
              </svg>
              <span className={styles.uploadRingIcon}>
                <UploadIcon />
              </span>
            </div>

            <h3 className={`${styles.statusTitle} ${styles.statusTitleUpload}`}>Enviando seu arquivo...</h3>
            <p className={styles.statusText}>
              Assim que o envio terminar, a IA começa a mapear os dados <strong>em segundo plano</strong>.
            </p>

            {selectedFile && (
              <div className={styles.uploadFileChip}>
                <FileIcon />
                <strong title={selectedFile.name}>{selectedFile.name}</strong>
                <span>· {smartRowsText}</span>
              </div>
            )}

            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${uploadProgress}%` }} />
            </div>
            <div className={styles.progressLabels}>
              <span>{uploadProgress}% enviado</span>
              <span>Não feche esta janela até o envio concluir.</span>
            </div>
          </div>
        </div>,
        document.getElementById("modal-root")
      )
    }

    return ReactDom.createPortal(
      <div className={styles.modalBackdrop}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.validatingOverlay}>
            <div className={styles.spinner} />
            <p className={styles.validatingText}>Validando arquivo com IA...</p>
            <p className={styles.validatingSubtext}>
              Estamos analisando o cabeçalho e os dados do seu arquivo para mapear as colunas automaticamente.
            </p>
          </div>
        </div>
      </div>,
      document.getElementById("modal-root")
    )
  }

  if (isSmart) {
    return ReactDom.createPortal(
      <div className={styles.modalBackdrop} onClick={onClose}>
        <div
          className={`${styles.modalContent} ${styles.modalSmart}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.smartHeader}>
            <span className={styles.smartHeaderIcon}>
              <img src={SparklesIcon} alt="" />
            </span>
            <div className={styles.smartHeaderText}>
              <h3>Importação Inteligente</h3>
              <p>Envie qualquer planilha e deixe a IA organizar os dados por você.</p>
            </div>
            <button type="button" className={styles.smartClose} onClick={onClose} aria-label="Fechar">
              ×
            </button>
          </div>

          <div className={styles.smartBody}>
            <div className={styles.smartCards}>
              <div className={styles.smartCard}>
                <h4>Qualquer formato</h4>
                <p>Planilhas .csv ou .xlsx, mesmo com dados de tipos misturados — a IA identifica e mapeia tudo.</p>
              </div>
              <div className={styles.smartCard}>
                <h4>Pode levar um tempo</h4>
                <p>Arquivos grandes demoram um pouco mais que o normal. Você não precisa esperar na tela.</p>
              </div>
              <div className={styles.smartCard}>
                <h4>A gente te avisa</h4>
                <p>Continue usando o sistema — você recebe uma notificação assim que a importação terminar.</p>
              </div>
            </div>

            <h4 className={styles.smartSectionTitle}>Selecione o arquivo que deseja importar</h4>

            {selectedFile ? (
              <div className={styles.selectedFileCard}>
                <span className={styles.selectedFileIcon}>
                  <FileIcon />
                </span>
                <div className={styles.selectedFileMeta}>
                  <strong title={selectedFile.name}>{selectedFile.name}</strong>
                  <span>{formatBytes(selectedFile.size)} · {smartRowsText}</span>
                </div>
                <button type="button" className={styles.removeFileBtn} onClick={handleRemoveFile}>
                  Trocar arquivo
                </button>
              </div>
            ) : (
              <div
                className={`${styles.fileUploadContainer} ${styles.smartDropzone}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <label htmlFor="file-upload">
                  <span className={styles.uploadIconWrap}>
                    <UploadIcon />
                  </span>

                  <h4>Arraste um arquivo aqui ou</h4>

                  <Button
                    text="Clique para selecionar"
                    variation="primary"
                    size="big"
                    onClick={handleButtonClick}
                    disabled={isValidating}
                  />

                  <span className={styles.fileUploadDescription}>
                    Aceitamos <b>.csv</b> e <b>.xlsx</b> — inclusive planilhas com dados de vários tipos misturados.
                    <br />
                    Para arquivos <b>.csv</b>, use de preferência o separador <b>;</b> (ponto e vírgula).
                  </span>
                </label>

                <input
                  ref={fileInputRef}
                  type="file"
                  id="file-upload"
                  onChange={handleFileChange}
                  disabled={isValidating}
                  accept=".csv,.xlsx"
                />
              </div>
            )}

            {validationError && (
              <div className={styles.errorBanner}>
                <p>{validationError}</p>
              </div>
            )}

            <label className={styles.manualValidationOption}>
              <input
                type="checkbox"
                checked={manualValidation}
                onChange={(e) => setManualValidation(e.target.checked)}
                disabled={isValidating}
              />
              <span>
                <strong>Quero revisar o mapeamento manualmente</strong> quando a IA estiver insegura.
                Se desmarcado, o arquivo é importado automaticamente.
              </span>
            </label>
          </div>

          <div className={styles.smartFooter}>
            <div className={styles.smartFooterNote}>
              <BellIcon />
              <span>Avisamos por notificação assim que terminar.</span>
            </div>
            <div className={styles.smartFooterActions}>
              <Button
                text="Cancelar"
                variation="secondary"
                size="big"
                onClick={onClose}
              />
              <Button
                text="Importar com IA"
                icon={SparklesIcon}
                variation="primary"
                size="big"
                onClick={handleSmartImport}
                disabled={!selectedFile || isValidating}
              />
            </div>
          </div>
        </div>
      </div>,
      document.getElementById("modal-root")
    )
  }

  return ReactDom.createPortal(
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <h3>{FILES_MAPPING[fileType].modalTile}</h3>

        <p>
          Importe uma planilha própria ou use nosso modelo recomendado.
          <br />
          Depois do envio, o sistema tentará identificar e relacionar automaticamente as colunas do arquivo com os campos de faturamento.
          <br />
          <strong>Usar o modelo recomendado ajuda a evitar ajustes manuais e facilita a importação.</strong>
        </p>

        {fileType === "faturamento" && (
          <div className={styles.infoBanner}>
            <p>
              Os dados de faturamento são utilizados nos indicadores financeiros. Na falta de algum campo, esses indicadores podem ficar incompletos ou sem dados.
            </p>
          </div>
        )}

        <a href={`/model-files/${FILES_MAPPING[fileType].file}`} download>
          <Button
            text="Baixar modelo de planilha"
            variation="secondary"
            size="big"
            icon={DownloadIcon}
            className={styles.downloadTemplateButton}
            styling={{
              width: "100%",
              justifyContent: "center",
              backgroundColor: "#3A8FD8"
            }}
          />
        </a>

        <div className={styles.modalContentAction}>
          <h4>Caso o arquivo já esteja no padrão, envie utilizando o campo abaixo.</h4>

          <div
            className={styles.fileUploadContainer}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <label htmlFor="file-upload">
              <img src={DocumentCirclePlusIcon} alt="Upload" />

              <h4>Arraste um arquivo aqui ou</h4>

              <Button
                text="Clique aqui para selecionar"
                variation="primary"
                size="big"
                onClick={handleButtonClick}
                disabled={isValidating}
              />

              <span className={styles.fileUploadDescription}>
                Arquivos aceitos: <b>.csv</b> e <b>.xlsx</b>.
                <br />
                Para arquivos <b>.csv</b>, use preferencialmente o separador <b>;</b> (ponto-vírgula).
              </span>
            </label>

            <input
              ref={fileInputRef}
              type="file"
              id="file-upload"
              onChange={handleFileChange}
              disabled={isValidating}
              accept=".csv,.xlsx"
            />
          </div>

          <div className={styles.selectedFileInfo}>
            {selectedFile ? (
              <p>
                <strong>Arquivo selecionado:</strong> {selectedFile.name}
              </p>
            ) : (
              <p className={styles.noFileSelected}>
                Nenhum arquivo selecionado
              </p>
            )}

            {validationError && (
              <div className={styles.errorBanner}>
                <p>{validationError}</p>
              </div>
            )}

            <div className={styles.actionButtons}>
              <Button
                text="Cancelar"
                variation="secondary"
                size="big"
                onClick={onClose}
                styling={{
                  flex: 1,
                  justifyContent: "center"
                }}
              />
              <Button
                text="Validar e mapear colunas"
                variation="primary"
                size="big"
                onClick={handleValidateAndProceed}
                disabled={!selectedFile || isValidating}
                styling={{
                  flex: 1,
                  justifyContent: "center"
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.getElementById("modal-root")
  )
}

export default ImportDataModal
